import {
  BiDisplayFormat,
  BiDisplayFormatKind,
  BiEmptyAs,
  BiFormatSpec,
} from '@euler/model/bi/format';
import {
  BiCell,
  BiFieldRole,
  BiFieldType,
  BiResultColumn,
} from '@euler/model/bi/result';
import {
  BiAxisSide,
  BiKpiCompare,
  BiSeriesType,
  BiVizSpec,
  BiVizType,
} from '@euler/model/bi/viz';
import {
  BiGalleryCategory,
  BiGalleryCategoryKey,
  BiGalleryDashboardCard,
} from '../functions/biGalleryTypes';
import {
  cents,
  kGalleryFields as f,
  galleryReady,
  galleryResult,
  kGalleryMonths,
} from './biGalleryFields';

// 数字格式: every value below is raw (a rate a fraction, money in cents,
// time in minutes); only the format decides how it reads.

const K = BiDisplayFormatKind;

function measure(
  key: string,
  zh: string,
  format: BiDisplayFormat,
): BiResultColumn {
  return {
    key,
    label: { zh, en: key },
    type: BiFieldType.Number,
    role: BiFieldRole.Measure,
    format,
  };
}

const rate = measure('conversion_rate', '转化率', { kind: K.Percent });
const revenue = measure('revenue__sum', '营收', { kind: K.Currency });
const reworks = measure('rework_count', '返工次数', {
  kind: K.Number,
  decimals: 0,
  unit: '次',
});
const vehicles = measure('vehicle_count', '车辆数', {
  kind: K.Number,
  decimals: 0,
  unit: '台',
});
const mileage = measure('mileage__avg', '平均进店里程', {
  kind: K.Number,
  unit: 'km',
});
const jobTime = measure('job_minutes__avg', '平均施工时长', {
  kind: K.Duration,
});

const kThisMonth = { from: '2026-10-01', to: '2026-10-17' };
const kLastMonth = { from: '2026-09-01', to: '2026-09-17' };

function kpi(
  title: string,
  column: BiResultColumn,
  current: BiCell,
  previous: BiCell,
  format?: BiFormatSpec,
): BiGalleryDashboardCard {
  return {
    surface: 'dashboard',
    title,
    viz: {
      type: BiVizType.Kpi,
      value: column.key,
      compare: BiKpiCompare.PreviousPeriod,
      ...(format ? { format } : {}),
    },
    data: galleryReady(galleryResult([column], [[current]], kThisMonth), {
      previous: galleryResult([column], [[previous]], kLastMonth),
    }),
  };
}

const months = kGalleryMonths.slice(4);
const monthly = galleryResult(
  [f.month, rate, revenue, reworks],
  months.map((month, i) => [
    month,
    [0.42, 0.455, 0.391, 0.5, 0.478, 0.512][i],
    cents([182000, 214500, 198300, 256800, 231200, 272300][i]),
    [12, 9, 14, 7, 8, 6][i],
  ]),
);

function chart(
  title: string,
  viz: BiVizSpec,
  rows = 4,
): BiGalleryDashboardCard {
  return {
    surface: 'dashboard',
    title,
    viz,
    data: galleryReady(monthly),
    rows,
  };
}

const items: [string, number, number | null, number, number][] = [
  ['常规保养', 0.52, 96800, 148, 42],
  ['钣金喷漆', 0.12, null, 41, 380],
  ['轮胎更换', 0.27, 88400, 96, 35],
  ['美容洗护', 0, 12720, 212, 30],
];
const itemResult = galleryResult(
  [f.serviceType, rate, revenue, vehicles, mileage, jobTime],
  items.map(([type, share, yuan, count, minutes], i) => [
    type,
    share,
    yuan === null ? null : cents(yuan),
    count,
    [32450.4, 58120.75, 41003, 12880.5][i],
    minutes,
  ]),
);

function table(
  title: string,
  formats?: Record<string, BiFormatSpec>,
): BiGalleryDashboardCard {
  return {
    surface: 'dashboard',
    title,
    viz: {
      type: BiVizType.Table,
      columns: itemResult.columns.map(column => ({ key: column.key })),
      ...(formats ? { formats } : {}),
    },
    data: galleryReady(itemResult),
    rows: 5,
  };
}

export const kFormatsCategory: BiGalleryCategory = {
  key: BiGalleryCategoryKey.Formats,
  name: '数字格式',
  description: '金额、百分比、时长、单位、万 / 亿、小数位、空值；涨跌按百分点',
  preview: kpi('本月转化率', rate, 0.5, 0.42),
  variants: [
    {
      key: 'rate-points',
      name: '比率 · 百分点',
      note: '0.5 → 50%，较上期按百分点',
      card: kpi('本月转化率', rate, 0.5, 0.42),
    },
    {
      key: 'money-compact',
      name: '金额 · 万',
      note: 'currency，compact，1 位小数',
      card: kpi('本月营收', revenue, cents(125430), cents(98760), {
        kind: K.Currency,
        compact: true,
        decimals: 1,
      }),
    },
    {
      key: 'money-whole',
      name: '金额 · 不要小数',
      note: 'currency，decimals = 0',
      card: kpi('本月营收', revenue, cents(125430.5), cents(98760), {
        kind: K.Currency,
        decimals: 0,
      }),
    },
    {
      key: 'count-unit',
      name: '次数 · 单位',
      note: 'number，unit = 次；轴上单位只写一次',
      card: chart('每月返工次数', {
        type: BiVizType.Bar,
        x: f.month.key,
        series: [{ key: reworks.key }],
        style: { dataLabels: true },
      }),
    },
    {
      key: 'rate-line',
      name: '比率折线',
      note: 'percent，decimals = 1；标签、提示、轴同一格式',
      card: chart('每月转化率', {
        type: BiVizType.Line,
        x: f.month.key,
        series: [{ key: rate.key }],
        formats: { [rate.key]: { kind: K.Percent, decimals: 1 } },
        style: { dataLabels: true },
      }),
    },
    {
      key: 'combo',
      name: '金额 + 比率',
      note: '左轴元、右轴 %',
      card: chart('每月营收与转化率', {
        type: BiVizType.Bar,
        x: f.month.key,
        series: [
          { key: revenue.key },
          { key: rate.key, as: BiSeriesType.Line, axis: BiAxisSide.Right },
        ],
      }),
    },
    {
      key: 'table-defaults',
      name: '表格 · 默认',
      note: '各列按数据集的格式；空值显示 —',
      card: table('服务类型明细'),
    },
    {
      key: 'table-overrides',
      name: '表格 · 卡片格式',
      note: '卡片格式优先于数据集格式：万、0 位小数、空值显示 0',
      card: table('服务类型明细（自定格式）', {
        [rate.key]: { kind: K.Percent, decimals: 2 },
        [revenue.key]: {
          kind: K.Currency,
          compact: true,
          emptyAs: BiEmptyAs.Zero,
        },
        [mileage.key]: { kind: K.Number, decimals: 0, unit: 'km' },
        [jobTime.key]: { kind: K.Number, decimals: 0, unit: '分钟' },
      }),
    },
  ],
};
